<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Button from '@uiKit/elements/Button.vue'
import Input from '@uiKit/elements/Input.vue'
import Select from '@uiKit/elements/Select.vue'
import Textarea from '@uiKit/elements/Textarea.vue'
import Message from '@uiKit/elements/Message.vue'
import FormField from '@uiKit/components/FormField.vue'
import { validateEmail } from '@shared/utils/validatorUtils'
import type { SelectOption } from '@shared/types/form'
import TicketAttachmentDropzone from './TicketAttachmentDropzone.vue'
import {
  SUPPORT_TICKET_DESCRIPTION_MAX_LENGTH,
  SUPPORT_TICKET_NAME_MAX_LENGTH,
  SUPPORT_TICKET_SUBJECT_MAX_LENGTH,
} from '../constants/supportTickets'
import type {
  SupportTicketFormMode,
  SupportTicketFormValues,
  TicketCategory,
} from '../types/supportTypes'

// The support ticket form (Figma 35143:147879), shared by the signed-in Contact
// Us dialog and the public contact page. It draws the body and the footer; the
// host owns the title and the close control.
//
// Public mode asks who to reply to. Signed-in mode shows the account's name and
// email read-only — seller-service takes them from the account, never from the
// form — and offers an attachment instead.
//
// Validation runs on blur and on submit, and the submit button stays enabled
// so an empty click explains itself (ux-principles: validation flow).
interface Props {
  mode: SupportTicketFormMode
  categories: TicketCategory[]
  /** The signed-in seller's name and email, shown read-only. Ignored in public mode. */
  contact?: { name: string; email: string } | null
  isCategoriesLoading?: boolean
  loading?: boolean
  apiError?: string
  submitLabel?: string
}

const props = withDefaults(defineProps<Props>(), {
  contact: null,
  isCategoriesLoading: false,
  loading: false,
  apiError: '',
  submitLabel: 'Submit Request',
})

const emit = defineEmits<{
  submit: [values: SupportTicketFormValues]
  cancel: []
}>()

type FieldKey = 'name' | 'email' | 'category' | 'subject' | 'description'
type TextField = 'name' | 'email' | 'categoryId' | 'subject' | 'description'

const form = reactive({
  name: '',
  email: '',
  categoryId: '',
  subject: '',
  description: '',
})
const attachment = ref<File | null>(null)

const isPublic = computed(() => props.mode === 'public')

const shownName = computed(() => (isPublic.value ? form.name : (props.contact?.name ?? '')))
const shownEmail = computed(() => (isPublic.value ? form.email : (props.contact?.email ?? '')))

const categoryOptions = computed<SelectOption[]>(() =>
  props.categories.map((category) => ({ label: category.title, value: category.id })),
)

const isSubmitted = ref(false)
const touched = ref(new Set<FieldKey>())
const hasEditedSinceApiError = ref(false)

watch(
  () => props.apiError,
  () => {
    hasEditedSinceApiError.value = false
  },
)

function touch(field: FieldKey) {
  touched.value = new Set([...touched.value, field])
}

function edit(field: TextField, value: string) {
  form[field] = value
  hasEditedSinceApiError.value = true
}

function requiredWithin(value: string, max: number, missing: string): string {
  if (!value.trim()) return missing
  return value.trim().length > max ? `Keep it under ${max} characters.` : ''
}

const rawErrors = computed<Record<FieldKey, string>>(() => ({
  name: isPublic.value
    ? requiredWithin(form.name, SUPPORT_TICKET_NAME_MAX_LENGTH, 'Enter your name.')
    : '',
  email: isPublic.value ? validateEmail(form.email) : '',
  category: form.categoryId ? '' : 'Choose a category.',
  subject: requiredWithin(
    form.subject,
    SUPPORT_TICKET_SUBJECT_MAX_LENGTH,
    'Enter a short subject.',
  ),
  description: requiredWithin(
    form.description,
    SUPPORT_TICKET_DESCRIPTION_MAX_LENGTH,
    'Tell us a little more about the issue.',
  ),
}))

const isFormValid = computed(() => Object.values(rawErrors.value).every((error) => !error))

function errorFor(field: FieldKey): string {
  return touched.value.has(field) || isSubmitted.value ? rawErrors.value[field] : ''
}

const visibleApiError = computed(() =>
  props.apiError && !hasEditedSinceApiError.value ? props.apiError : '',
)

function selectCategory(value: string) {
  edit('categoryId', value)
  touch('category')
}

function handleSubmit() {
  isSubmitted.value = true
  if (props.loading || !isFormValid.value) return
  emit('submit', {
    categoryId: form.categoryId,
    subject: form.subject,
    description: form.description,
    name: isPublic.value ? form.name : '',
    email: isPublic.value ? form.email : '',
    attachment: isPublic.value ? null : attachment.value,
  })
}
</script>

<template>
  <Box direction="col" class="ticket-form">
    <Box direction="col" gap="1.25rem" class="ticket-form__body">
      <Box class="ticket-form__row">
        <FormField label="Name" :error="errorFor('name')">
          <Input
            :model-value="shownName"
            placeholder="Name"
            :disabled="loading || !isPublic"
            :invalid="!!errorFor('name')"
            @update:model-value="edit('name', $event)"
            @blur="touch('name')"
          />
        </FormField>
        <FormField label="Email ID" :error="errorFor('email')">
          <Input
            :model-value="shownEmail"
            type="email"
            placeholder="Example@gmail.com"
            :disabled="loading || !isPublic"
            :invalid="!!errorFor('email')"
            @update:model-value="edit('email', $event)"
            @blur="touch('email')"
          />
        </FormField>
      </Box>

      <Box class="ticket-form__row ticket-form__row--category">
        <FormField label="Category" :error="errorFor('category')">
          <Select
            :model-value="form.categoryId"
            :options="categoryOptions"
            placeholder="Select Category"
            :disabled="loading || isCategoriesLoading"
            :invalid="!!errorFor('category')"
            @update:model-value="selectCategory"
          />
        </FormField>
        <FormField label="Subject" :error="errorFor('subject')">
          <Input
            :model-value="form.subject"
            placeholder="Brief description of your issue"
            :disabled="loading"
            :invalid="!!errorFor('subject')"
            @update:model-value="edit('subject', $event)"
            @blur="touch('subject')"
          />
        </FormField>
      </Box>

      <FormField label="Description" :error="errorFor('description')">
        <Textarea
          :model-value="form.description"
          placeholder="Describe your issue in detail..."
          :rows="3"
          :disabled="loading"
          :invalid="!!errorFor('description')"
          @update:model-value="edit('description', $event)"
          @blur="touch('description')"
        />
      </FormField>

      <FormField v-if="!isPublic" label="Attachment" optional>
        <TicketAttachmentDropzone v-model="attachment" :disabled="loading" />
      </FormField>

      <Message
        v-if="visibleApiError"
        color="danger"
        variant="outlined"
        :message="visibleApiError"
      />
    </Box>

    <Box flex gap="3" class="ticket-form__footer">
      <Button
        label="Cancel"
        variant="outline"
        color="secondary"
        size="large"
        block
        :disabled="loading"
        @click="emit('cancel')"
      />
      <Button :label="submitLabel" size="large" block :loading="loading" @click="handleSubmit" />
    </Box>
  </Box>
</template>

<style scoped>
.ticket-form__body {
  padding: 1.75rem;
}

.ticket-form__row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.25rem;
}

/* The design gives Category a fixed column (292px of the 604px body) and lets
   Subject take the rest. */
.ticket-form__row--category {
  grid-template-columns: 292px minmax(0, 1fr);
}

.ticket-form__footer {
  padding: 1.25rem 1.75rem 1.5rem;
  border-top: 1px solid var(--p-content-border-color);
}

@media (max-width: 992px) {
  .ticket-form__body {
    padding: 1.25rem 1rem;
  }

  .ticket-form__row,
  .ticket-form__row--category {
    grid-template-columns: minmax(0, 1fr);
  }

  .ticket-form__footer {
    flex-direction: column-reverse;
    padding: 1rem;
  }
}
</style>
